import React,{useCallback,useEffect,useState}from"react";
import{Box,Button,Checkbox,Dialog,DialogActions,DialogContent,DialogTitle,FormControlLabel,IconButton,TextField,Typography}from"@mui/material";
import{AddRounded,CloseRounded,EditOutlined,LockResetOutlined,PeopleAltOutlined}from"@mui/icons-material";
import{useAuth}from"../../../context/AuthContext";
import{useAlert}from"../../../context/AlertContext";

const initialForm={company_name:"",representative:"",email:"",phone:"",service_type:"",ruc:"",active:true};

export default function Administrators(){
  const{authFetch}=useAuth();
  const{showSuccess,showError,showWarning}=useAlert();
  const[items,setItems]=useState([]);
  const[supervisors,setSupervisors]=useState([]);
  const[assignedSupervisors,setAssignedSupervisors]=useState({});
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[actionId,setActionId]=useState(null);
  const[open,setOpen]=useState(false);
  const[assignOpen,setAssignOpen]=useState(false);
  const[editing,setEditing]=useState(null);
  const[selectedAdmin,setSelectedAdmin]=useState(null);
  const[selectedSupervisors,setSelectedSupervisors]=useState([]);
  const[form,setForm]=useState(initialForm);

  const loadData=useCallback(async()=>{
    setLoading(true);
    try{
      const response=await authFetch("/api/login/administrators/");
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||"No se pudieron cargar los administradores.");
      const administrators=Array.isArray(data)?data:[];
      setItems(administrators);
      const assignments=await Promise.all(administrators.map(async administrator=>{
        try{
          const assignedResponse=await authFetch(`/api/login/administrators/${administrator.public_id}/supervisors/`);
          const assignedData=await assignedResponse.json();
          return[administrator.public_id,assignedResponse.ok&&Array.isArray(assignedData)?assignedData:[]];
        }catch{return[administrator.public_id,[]];}
      }));
      setAssignedSupervisors(Object.fromEntries(assignments));
    }catch(error){showError(error.message||"No se pudieron cargar los administradores.");}
    finally{setLoading(false);}
  },[authFetch,showError]);

  useEffect(()=>{loadData();},[loadData]);

  const handleChange=e=>{
    const{name,value,checked,type}=e.target;
    setForm(prev=>({...prev,[name]:type==="checkbox"?checked:value}));
  };

  const openCreate=()=>{setEditing(null);setForm(initialForm);setOpen(true);};

  const openEdit=item=>{
    setEditing(item);
    setForm({company_name:item.company_name||"",representative:item.representative||"",email:item.email||"",phone:item.phone||"",service_type:item.service_type||"",ruc:item.ruc||"",active:Boolean(item.active)});
    setOpen(true);
  };

  const closeDialog=()=>{if(saving)return;setOpen(false);setEditing(null);setForm(initialForm);};

  const submit=async()=>{
    if(!form.company_name.trim()||!form.representative.trim()||!form.email.trim()||!form.phone.trim()||!form.service_type.trim()||!form.ruc.trim()){showWarning("Complete todos los campos obligatorios.");return;}
    if(!/^\d{13}$/.test(form.ruc)){showWarning("El RUC debe contener 13 dígitos.");return;}
    setSaving(true);
    try{
      const response=await authFetch(editing?`/api/login/administrators/${editing.public_id}/`:"/api/login/administrators/",{
        method:editing?"PATCH":"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({company_name:form.company_name.trim(),representative:form.representative.trim(),email:form.email.trim().toLowerCase(),phone:form.phone.trim(),service_type:form.service_type.trim(),ruc:form.ruc.trim(),active:form.active}),
      });
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||data.company_name?.[0]||data.representative?.[0]||data.email?.[0]||data.phone?.[0]||data.service_type?.[0]||data.ruc?.[0]||"No se pudo guardar el administrador.");
      showSuccess(editing?"Administrador actualizado.":"Administrador creado e invitación enviada.");
      setOpen(false);setEditing(null);setForm(initialForm);await loadData();
    }catch(error){showError(error.message||"No se pudo guardar el administrador.");}
    finally{setSaving(false);}
  };

  const toggleStatus=async item=>{
    setActionId(`status-${item.public_id}`);
    try{
      const response=await authFetch(`/api/login/administrators/${item.public_id}/`,{method:"PATCH",headers:{"Content-Type":"application/json"},body:JSON.stringify({active:!item.active})});
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||"No se pudo cambiar el estado.");
      showSuccess(item.active?"Administrador inactivado.":"Administrador activado.");
      await loadData();
    }catch(error){showError(error.message||"No se pudo cambiar el estado.");}
    finally{setActionId(null);}
  };

  const resetPassword=async item=>{
    setActionId(`reset-${item.public_id}`);
    try{
      const response=await authFetch(`/api/login/administrators/${item.public_id}/reset-password/`,{method:"POST"});
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||"No se pudo restablecer la contraseña.");
      showSuccess(data.detail||"Nueva contraseña enviada.");
    }catch(error){showError(error.message||"No se pudo restablecer la contraseña.");}
    finally{setActionId(null);}
  };

  const openAssignments=async item=>{
    setSelectedAdmin(item);setAssignOpen(true);
    try{
      const[allResponse,assignedResponse]=await Promise.all([authFetch("/api/login/supervisors/"),authFetch(`/api/login/administrators/${item.public_id}/supervisors/`)]);
      const allData=await allResponse.json();
      const assignedData=await assignedResponse.json();
      if(!allResponse.ok)throw new Error(allData.detail||"No se pudieron cargar los supervisores.");
      if(!assignedResponse.ok)throw new Error(assignedData.detail||"No se pudieron cargar las asignaciones.");
      setSupervisors(Array.isArray(allData)?allData.filter(supervisor=>supervisor.active):[]);
      setSelectedSupervisors(Array.isArray(assignedData)?assignedData.map(supervisor=>supervisor.public_id):[]);
    }catch(error){showError(error.message||"No se pudieron cargar los supervisores.");}
  };

  const closeAssignments=()=>{if(saving)return;setAssignOpen(false);setSelectedAdmin(null);setSelectedSupervisors([]);};
  const toggleSupervisor=publicId=>setSelectedSupervisors(prev=>prev.includes(publicId)?prev.filter(item=>item!==publicId):[...prev,publicId]);

  const saveAssignments=async()=>{
    if(!selectedAdmin)return;
    setSaving(true);
    try{
      const response=await authFetch(`/api/login/administrators/${selectedAdmin.public_id}/supervisors/`,{method:"PUT",headers:{"Content-Type":"application/json"},body:JSON.stringify({supervisor_ids:selectedSupervisors})});
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||"No se pudieron guardar las asignaciones.");
      setAssignedSupervisors(prev=>({...prev,[selectedAdmin.public_id]:Array.isArray(data)?data:[]}));
      showSuccess("Supervisores asignados correctamente.");
      setAssignOpen(false);setSelectedAdmin(null);setSelectedSupervisors([]);
    }catch(error){showError(error.message||"No se pudieron guardar las asignaciones.");}
    finally{setSaving(false);}
  };

  const fieldSx={"& .MuiOutlinedInput-root":{borderRadius:2}};

  return(
    <Box>
      <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:2,mb:2.5}}>
        <Typography sx={{fontSize:24,fontWeight:700,color:"#182230"}}>Administradores</Typography>
        <Button variant="contained" startIcon={<AddRounded/>} onClick={openCreate} sx={{bgcolor:"#123f6d",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none","&:hover":{bgcolor:"#0e345b",boxShadow:"none"}}}>Crear administrador</Button>
      </Box>

      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,overflowX:"auto"}}>
        <Box sx={{minWidth:1500}}>
          <Box sx={{display:"grid",gridTemplateColumns:"1.3fr 1.1fr 1.4fr .9fr 1fr 1.1fr 1.5fr .7fr 1.8fr",gap:2,px:2.5,py:1.5,bgcolor:"#123f6d",color:"#fff"}}>
            {["Empresa","Representante","Correo","RUC","Teléfono","Servicio","Supervisores","Estado","Acciones"].map(label=><Typography key={label} sx={{fontSize:13,fontWeight:700}}>{label}</Typography>)}
          </Box>

          {loading&&<Box sx={{py:6,textAlign:"center"}}><Typography sx={{fontSize:14,color:"#667085"}}>Cargando administradores...</Typography></Box>}
          {!loading&&items.length===0&&<Box sx={{py:6,textAlign:"center"}}><Typography sx={{fontSize:14,color:"#667085"}}>No hay administradores registrados.</Typography></Box>}

          {!loading&&items.map(item=>{
            const assigned=assignedSupervisors[item.public_id]||[];
            return(
              <Box key={item.public_id} sx={{display:"grid",gridTemplateColumns:"1.3fr 1.1fr 1.4fr .9fr 1fr 1.1fr 1.5fr .7fr 1.8fr",gap:2,px:2.5,py:1.5,alignItems:"center",borderBottom:"1px solid #edf1f5","&:last-child":{borderBottom:"none"}}}>
                <Typography translate="no" className="notranslate" sx={{fontSize:13.5,fontWeight:700,color:"#182230"}}>{item.company_name}</Typography>
                <Typography sx={{fontSize:13.5,color:"#344054"}}>{item.representative}</Typography>
                <Typography sx={{fontSize:13.5,color:"#344054",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.email}</Typography>
                <Typography sx={{fontSize:13.5,color:"#344054"}}>{item.ruc}</Typography>
                <Typography sx={{fontSize:13.5,color:"#344054"}}>{item.phone}</Typography>
                <Typography translate="no" className="notranslate" sx={{fontSize:13.5,color:"#344054"}}>{item.service_type}</Typography>
                <Box>{assigned.length>0?<Box sx={{display:"flex",flexDirection:"column",gap:.3}}>{assigned.map(supervisor=><Typography key={supervisor.public_id} translate="no" className="notranslate" sx={{fontSize:12.5,fontWeight:600,color:"#344054",lineHeight:1.3}}>{supervisor.name}</Typography>)}</Box>:<Typography sx={{fontSize:12.5,color:"#98a2b3"}}>Sin asignar</Typography>}</Box>
                <Box sx={{width:"fit-content",px:1.1,py:.4,borderRadius:5,bgcolor:item.active?"#e9f7ef":"#fdecec",color:item.active?"#207a45":"#b42318",fontSize:12,fontWeight:700}}>{item.active?"Activo":"Inactivo"}</Box>
                <Box sx={{display:"flex",gap:.5,alignItems:"center"}}>
                  <IconButton size="small" onClick={()=>openEdit(item)} title="Editar"><EditOutlined sx={{fontSize:18}}/></IconButton>
                  <IconButton size="small" onClick={()=>openAssignments(item)} title="Asignar supervisores"><PeopleAltOutlined sx={{fontSize:18}}/></IconButton>
                  <IconButton size="small" disabled={actionId!==null} onClick={()=>resetPassword(item)} title="Restablecer contraseña"><LockResetOutlined sx={{fontSize:18}}/></IconButton>
                  <Button size="small" variant="outlined" disabled={actionId!==null} onClick={()=>toggleStatus(item)} sx={{borderRadius:2,textTransform:"none",fontWeight:700,fontSize:11.5,color:item.active?"#b42318":"#207a45",borderColor:item.active?"#f0b7b3":"#a9d8bc","&:hover":{borderColor:item.active?"#b42318":"#207a45",bgcolor:item.active?"#fff6f5":"#f0faf4"}}}>{actionId===`status-${item.public_id}`?"Procesando...":item.active?"Inactivar":"Activar"}</Button>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md" disableScrollLock PaperProps={{sx:{borderRadius:3,boxShadow:"none",border:"1px solid #dce3ea"}}}>
        <DialogTitle sx={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:20,fontWeight:700,color:"#182230"}}>{editing?"Editar administrador":"Crear administrador"}<IconButton onClick={closeDialog} disabled={saving}><CloseRounded/></IconButton></DialogTitle>
        <DialogContent>
          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"1fr 1fr"},gap:2,pt:1}}>
            <TextField label="Nombre de la empresa" name="company_name" value={form.company_name} onChange={handleChange} size="small" fullWidth sx={fieldSx}/>
            <TextField label="Representante" name="representative" value={form.representative} onChange={handleChange} size="small" fullWidth sx={fieldSx}/>
            <TextField label="Correo del administrador" name="email" type="email" value={form.email} onChange={handleChange} size="small" fullWidth sx={fieldSx}/>
            <TextField label="Teléfono" name="phone" value={form.phone} onChange={handleChange} size="small" fullWidth sx={fieldSx}/>
            <TextField label="Tipo de servicio" name="service_type" value={form.service_type} onChange={handleChange} size="small" fullWidth sx={fieldSx}/>
            <TextField label="RUC" name="ruc" value={form.ruc} onChange={e=>setForm(prev=>({...prev,ruc:e.target.value.replace(/\D/g,"").slice(0,13)}))} inputProps={{maxLength:13}} size="small" fullWidth sx={fieldSx}/>
            <FormControlLabel control={<Checkbox name="active" checked={form.active} onChange={handleChange} size="small"/>} label={<Typography sx={{fontSize:13.5}}>Cuenta activa</Typography>}/>
          </Box>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2.5}}>
          <Button onClick={closeDialog} disabled={saving} sx={{textTransform:"none",color:"#667085",fontWeight:700}}>Cancelar</Button>
          <Button variant="contained" onClick={submit} disabled={saving} sx={{bgcolor:"#123f6d",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none","&:hover":{bgcolor:"#0e345b",boxShadow:"none"}}}>{saving?"Guardando...":editing?"Guardar cambios":"Crear administrador"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignOpen} onClose={closeAssignments} fullWidth maxWidth="sm" disableScrollLock PaperProps={{sx:{borderRadius:3,boxShadow:"none",border:"1px solid #dce3ea"}}}>
        <DialogTitle sx={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:20,fontWeight:700,color:"#182230"}}>Asignar supervisores<IconButton onClick={closeAssignments} disabled={saving}><CloseRounded/></IconButton></DialogTitle>
        <DialogContent>
          <Typography translate="no" className="notranslate" sx={{fontSize:13.5,fontWeight:700,color:"#182230",mb:2}}>{selectedAdmin?.company_name}</Typography>
          {supervisors.length===0&&<Typography sx={{fontSize:13.5,color:"#667085"}}>No existen supervisores activos.</Typography>}
          <Box sx={{display:"flex",flexDirection:"column",gap:.5}}>
            {supervisors.map(item=><FormControlLabel key={item.public_id} control={<Checkbox size="small" checked={selectedSupervisors.includes(item.public_id)} onChange={()=>toggleSupervisor(item.public_id)}/>} label={<Box><Typography translate="no" className="notranslate" sx={{fontSize:13.5,fontWeight:700,color:"#182230"}}>{item.name}</Typography><Typography sx={{fontSize:12.5,color:"#667085"}}>{item.email} · {item.phone}</Typography></Box>} sx={{m:0,p:1,borderBottom:"1px solid #edf1f5"}}/>)}
          </Box>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2.5}}>
          <Button onClick={closeAssignments} disabled={saving} sx={{textTransform:"none",color:"#667085",fontWeight:700}}>Cancelar</Button>
          <Button variant="contained" onClick={saveAssignments} disabled={saving} sx={{bgcolor:"#123f6d",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none","&:hover":{bgcolor:"#0e345b",boxShadow:"none"}}}>{saving?"Guardando...":"Guardar"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}